import React, { useState } from 'react';
// @ts-ignore
import MicRecorder from 'mic-recorder-to-mp3';
import Fab from '@mui/material/Fab';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SendIcon from '@mui/icons-material/Send';
import { IconButton, TextField } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { analyzeVoice } from '../../../../api';
import RecordingIcon from './RecordingIcon';
import Timer from './Timer';
import { isUserUnsubscribed } from '../../../../utils/helpers';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ClearIcon from '@mui/icons-material/Clear';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { UserType } from '../../../../common/commonTypes';
import { useTranslation } from 'react-i18next';

type MealInput2Type = {
    user: UserType;
    analyzing: boolean;
    handleAnalyzeMeal: (meal: string) => void;
    showBarCodeScanner: () => void;
    showCameraModal: () => void;
};

const recorder = new MicRecorder({ bitRate: 128 });

const MealInput2 = (props: MealInput2Type) => {
    const { user, showBarCodeScanner, showCameraModal, handleAnalyzeMeal, analyzing } = props;
    const { t, i18n } = useTranslation();

    const [meal, setMeal] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [recognizing, setRecognizing] = useState(false);

    const isIOSDevice = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const [isFocused, setIsFocused] = useState(false); // FOR IPHONE

    const startRecording = async () => {
        try {
            await recorder.start();
            setIsRecording(true);
        } catch (error) {
            console.error('Error accessing the microphone:', error);
            alert('An error occurred while trying to access your microphone.');
        }

        window.gtag?.('event', 'started voice recording', {});
    };

    const stopRecording = () => {
        recorder
            .stop()
            .getMp3()
            // @ts-ignore
            .then(async ([buffer, blob]) => {
                // TODO: buffer contains raw audio data and might be used for visualization
                setIsRecording(false);
                if (blob.size) {
                    //  otherwise it calls BE twice
                    await sendAudioToBackend(blob);
                }
            })
            .catch((error: any) => {
                setIsRecording(false);
                console.error('Failed to retrieve audio:', error);
            });

        window.gtag?.('event', 'stopped voice recording', {});
    };

    const sendAudioToBackend = async (blob: Blob) => {
        setRecognizing(true);
        try {
            const fileType = 'audio/mp3'; // mic-recorder-to-mp3 produces MP3
            const file = new File([blob], 'recording.mp3', { type: fileType });

            const formData = new FormData();
            formData.append('file', file);

            const response: any = await analyzeVoice(formData);
            setMeal(response.data.text || '');
        } catch (error) {
            console.error('Failed to send voice for analysis:', error);
            alert('Failed to send voice for analysis.');
        } finally {
            setRecognizing(false);
        }
    };

    const onMealChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMeal(e.target.value);
    };

    const onClearClick = () => {
        setMeal('');
    };

    const onAnalyzeClick = () => {
        handleAnalyzeMeal(meal);
        setMeal('');

        window.gtag?.('event', 'sent text for analysis', {
            meal,
        });
    };

    return (
        <section
            className="flex absolute left-0 right-0 p-2 bg-white transition-all duration-300 ease-in"
            style={{ bottom: '0px' }} // todo: test and see if you still need it
            // style={{ bottom: isIOSDevice && isFocused ? '70px' : '0px' }} // todo: test and see if you still need it
        >
            {/* LEFT-SIDE BUTTONS*/}
            <div className="flex flex-col content-center justify-evenly">
                {!isRecording && !analyzing && !recognizing && (
                    <IconButton color="primary" onClick={showCameraModal}>
                        <PhotoCameraIcon />
                    </IconButton>
                )}

                {!isRecording && !analyzing && !recognizing && (
                    <IconButton color="primary" onClick={showBarCodeScanner}>
                        <QrCodeScannerIcon />
                    </IconButton>
                )}
            </div>

            <div className="w-full mx-2">
                {/* RECORDING STATE */}
                {isRecording && !recognizing && !analyzing && (
                    <div
                        className="bg-neutral-100 rounded flex flex-col items-center justify-center relative"
                        onClick={stopRecording}
                        style={{ height: '125px' }}
                    >
                        {/*// TODO: POSSIBLY - MAKE A SEPARATE COMPONENT TO MAKE THEM ALL RED */}
                        <Timer
                            className="absolute left-2 top-2 text-xs text-gray-700"
                            secondsToStop={20}
                            onComplete={stopRecording}
                            isRunning
                        />
                        <RecordingIcon className="absolute" />
                        <IconButton aria-label="stop" color="default" onClick={stopRecording}>
                            <StopCircleIcon />
                        </IconButton>
                        <div className="text-center text-xs">{t('recording_click_stop')}</div>
                    </div>
                )}

                {/* LOADING/RECOGNIZING/ANALYZING STATES */}
                {!isRecording && (recognizing || analyzing) && (
                    <div
                        className="bg-neutral-100 rounded flex flex-col items-center justify-center relative"
                        style={{ height: '125px' }}
                    >
                        <div className="mb-2 text-center text-gray-400">
                            <CircularProgress size={30} sx={{ color: 'inherit' }} />
                        </div>
                        <div className="text-center text-xs text-gray-500">
                            {recognizing ? t('converting_to_text') : t('analyzing')}
                        </div>
                    </div>
                )}

                {/* TEXT INPUT STATE */}
                {!isRecording && !analyzing && !recognizing && (
                    <TextField
                        className="w-full"
                        variant="filled"
                        multiline
                        rows={4}
                        //@ts-ignore
                        placeholder={t('what_did_you_eat')}
                        value={meal}
                        onChange={onMealChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                )}
            </div>

            {/* RIGHT-SIDE FAB BUTTONS */}
            {!isRecording && !analyzing && !recognizing && (
                <div className="flex flex-col align-center justify-evenly">
                    {meal ? (
                        <>
                            <IconButton
                                aria-label="clear"
                                size="small"
                                color="default"
                                onClick={onClearClick}
                            >
                                <ClearIcon />
                            </IconButton>
                            <Fab aria-label="send" color="primary" onClick={onAnalyzeClick}>
                                <SendIcon />
                            </Fab>
                        </>
                    ) : (
                        <Fab
                            aria-label="record"
                            color="primary"
                            onClick={startRecording}
                            disabled={analyzing}
                        >
                            <MicIcon />
                        </Fab>
                    )}
                </div>
            )}
        </section>
    );
};

export default MealInput2;
